/**
 * Stop Detail Modal Component
 * Modal showing detailed stop information
 */

import { Card, Badge, Button, getStatusBadgeVariant } from '../ui';
import { X, MapPin, Clock, Package, RotateCcw, Navigation, Banknote } from 'lucide-react';
import type { RouteStop } from '../../types';

interface StopDetailModalProps {
  stop: RouteStop | null;
  onClose: () => void;
  onReassign?: (taskUid: string) => void;
}

export default function StopDetailModal({
  stop,
  onClose,
  onReassign,
}: StopDetailModalProps) {
  if (!stop) return null;

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Baghdad',
    });
  };

  const getStopIcon = (type: RouteStop['type']) => {
    switch (type) {
      case 'pickup':
        return <Package className="h-5 w-5 text-green-600" />;
      case 'rto':
        return <RotateCcw className="h-5 w-5 text-yellow-600" />;
      case 'cash':
        return <Banknote className="h-5 w-5 text-amber-600" />;
      default:
        return <MapPin className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative z-10 w-full max-w-md mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              {getStopIcon(stop.type)}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                Stop #{stop.sequence}
              </h3>
              <p className="text-sm text-gray-500">{stop.task_uid}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="py-4 space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Status</span>
            <Badge variant={getStatusBadgeVariant(stop.status)}>
              {stop.status}
            </Badge>
          </div>

          {/* Type */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Type</span>
            <Badge variant={stop.type === 'pickup' ? 'success' : stop.type === 'cash' ? 'warning' : 'warning'}>
              {stop.type === 'cash' ? 'CASH' : stop.type.toUpperCase()}
            </Badge>
          </div>

          {/* Merchant */}
          {stop.merchant_name && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Merchant</span>
              <span className="text-sm font-medium text-gray-900">
                {stop.merchant_name}
              </span>
            </div>
          )}

          {/* Address */}
          <div>
            <span className="text-sm text-gray-600 block mb-1">Address</span>
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
              <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-900">
                {stop.address || `${stop.lat.toFixed(6)}, ${stop.lng.toFixed(6)}`}
              </span>
            </div>
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-600 block mb-1">ETA</span>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  {formatTime(stop.eta || stop.google_eta)}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-600 block mb-1">Service Time</span>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  {stop.service_time_min} min
                </span>
              </div>
            </div>
          </div>

          {/* Parcels */}
          {stop.parcel_count && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Parcels</span>
              <span className="text-sm font-medium text-gray-900">
                {stop.parcel_count}
              </span>
            </div>
          )}

          {/* Distance from previous */}
          {stop.distance_from_prev_km && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Distance from previous</span>
              <span className="text-sm font-medium text-gray-900">
                {stop.distance_from_prev_km.toFixed(1)} km
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="pt-4 border-t border-gray-200 flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            leftIcon={<Navigation className="h-4 w-4" />}
            onClick={() => {
              window.open(
                `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}`,
                '_blank'
              );
            }}
          >
            Directions
          </Button>
          {onReassign && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onReassign(stop.task_uid)}
            >
              Reassign
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
